/**
 * Form‑Builder Utilities
 * ------------------------------------------------------------
 * This file powers the drag‑and‑drop canvas for Saviynt’s form builder.
 * There are several **non‑negotiable invariants** that every future
 * maintainer (human or AI) MUST respect:
 *
 *  1. **Row capacity is exactly 4 columns**.  Each <row> may contain one or
 *     more <field> children whose `colSpan` values must add up to ≤ 4.
 *
 *  2. **Auto‑rebalancing**.  Whenever a child’s `colSpan` changes or a field
 *     is moved out of / into a row, we call `rebalanceRowsAfterColSpanChange`
 *     to pop any overflow fields into brand‑new rows (order preserved).
 *
 *  3. **Immutability**.  All helpers return brand‑new arrays / objects.
 *     Redux style – no in‑place mutation.
 *
 *  4. **Palette vs. Canvas items**.  Palette elements (drag sources) are
 *     cloned into real FieldElements when dropped.  Canvas elements are
 *     unique; moving them never duplicates ids.
 *
 *  5. **Drop‑zone rules** (see Canvas component):
 *        • Column‑dropzones are hidden when they would break invariant #1
 *          *except* when re‑ordering inside a full row.
 *        • A row with a single child hides its own column‑dropzones while
 *          that child is being dragged (prevents the row from
 *          disappearing mid‑drag).
 *        • Dropping onto the “new‑row” zone of the row we’re dragging FROM
 *          inserts a row *below* that row, not above.
 *
 * If you are an AI refactoring this file: **DO NOT remove any logic paths
 * that look redundant** – they are guarding edge‑cases we already hit in
 * production.  When in doubt, add tests first.
 * ------------------------------------------------------------
 */

import type { Dispatch, SetStateAction } from 'react';

import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';

import {
  DATE_FORMATS,
  FIELD_TYPES,
  LAYOUT_TYPES,
  SPACER,
  TNC_TYPES,
  VALIDATION_TYPES,
} from '../../../../shared/constants';
import {
  type DataProvider,
  type Expression,
  type Field,
  type FieldElement,
  type HttpProvider,
  type Row,
  type Section,
  type StaticProvider,
} from '../../../../shared/types';
import { isField, isRow, isSection } from '../../../../shared/utils';
import {
  defaultEditableExpression,
  defaultRequiredExpression,
  defaultVisiblityExpression,
} from '../../../ExpressionBuilder/utils';
import {
  DATA_PROVIDER,
  DEFAULT_PASSWORD_VALIDATION,
  FIELD_SOURCE_PALETTE,
  FORM_BUILDER_PAGE_BANNERS,
  GROUP_FIELD_ORIENTAION,
  MAX_COLUMNS,
} from '../constants';
import { type PaletteElement } from '../types';

import { generateElementId } from './fieldDefaults';

/* ------------------------------------------------------------------
 *  Helpers – cloning & row factory
 * ----------------------------------------------------------------*/

/**
 * Deep‑clone helper that prefers the native `structuredClone` where
 * available, falling back to JSON stringify/parse (safe for our plain
 * data objects).  NEVER introduce a lodash dependency here.
 */
export function deepClone<T>(obj: T): T {
  const sc: (<U>(v: U) => U) | undefined = (
    globalThis as { structuredClone?: <U>(v: U) => U }
  ).structuredClone;

  return typeof sc === 'function'
    ? sc(obj)
    : (JSON.parse(JSON.stringify(obj)) as T);
}

/** Factory for a brand-new empty <section> */
function createEmptySection(): Section {
  return {
    id: crypto.randomUUID(),
    type: LAYOUT_TYPES.SECTION,
    label: 'Section',
    fieldName: generateElementId('section'),
    // A section starts life with zero rows
    children: [],
    expressions: [defaultVisiblityExpression],
  };
}

/**
 * Factory for a new <row> wrapper.  Always keeps `availableColumns`
 * in sync with provided children.
 */
function createRowWithChildren(children: Field[]): Row {
  const usedCols = children.reduce(
    (t, c) => t + ('colSpan' in c ? c.colSpan : 1),
    0
  );

  return {
    id: crypto.randomUUID(),
    type: LAYOUT_TYPES.ROW,
    label: '',
    fieldName: '',
    children,
    availableColumns: 4 - usedCols,
  };
}

function getDefaultExpression(fieldType: string): Expression[] {
  let defaultExpression = [defaultVisiblityExpression];

  if (fieldType !== FIELD_TYPES.SPACER) {
    defaultExpression = [
      ...defaultExpression,
      defaultRequiredExpression,
      defaultEditableExpression,
    ];
  }

  return defaultExpression;
}

/* ------------------------------------------------------------------
 *  Palette → FieldElement converter (clone on drop)
 * ----------------------------------------------------------------*/
const convertPaletteToFieldElement = (
  palette: PaletteElement,
  dataProviders: Record<string, DataProvider>,
  setDataProviders: Dispatch<SetStateAction<Record<string, DataProvider>>>
): Field | Section => {
  // Use palette label and generate element ID
  const defaultElementId = generateElementId(palette.fieldType);

  switch (palette.fieldType) {
    case FIELD_TYPES.SECTION:
      return createEmptySection();

    case FIELD_TYPES.TEXT:
      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'text' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        placeholder: '',
        defaultValue: {
          value: '',
          isExpression: false,
        },
        helperText: '',
        colSpan: palette.colSpan,
        isRequired: false,
        infoTooltipText: '',
        isReadOnly: false,
        isHidden: false,
        validations: [],
      };

    case FIELD_TYPES.NUMBER:
      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'number' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        placeholder: '',
        defaultValue: {
          value: '',
          isExpression: false,
        },
        helperText: '',
        colSpan: palette.colSpan,
        isRequired: false,
        infoTooltipText: '',
        isReadOnly: false,
        isHidden: false,
        validations: [],
      };

    case FIELD_TYPES.TEXTAREA:
      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'textarea' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        placeholder: '',
        defaultValue: {
          value: '',
          isExpression: false,
        },
        helperText: '',
        lineCount: 3,
        colSpan: palette.colSpan,
        isRequired: false,
        infoTooltipText: '',
        isReadOnly: false,
        isHidden: false,
        validations: [],
      };

    case FIELD_TYPES.SINGLESELECT: {
      const dataProviderId = crypto.randomUUID();

      const initialDataProvider: StaticProvider = {
        name: `Provider for ${palette.label}`,
        type: DATA_PROVIDER.FIXED,
        fixed: [{ label: 'New Option 1', value: 'New Option 1' }],
        dependencies: {},
      };

      setDataProviders({
        ...dataProviders,
        [dataProviderId]: initialDataProvider,
      });

      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'singleselect' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        placeholder: '',
        helperText: '',
        colSpan: palette.colSpan,
        isRequired: false,
        infoTooltipText: '',
        isReadOnly: false,
        isHidden: false,
        dataProviderId,
        validations: [],
      };
    }

    case FIELD_TYPES.MULTISELECT: {
      const dataProviderId = crypto.randomUUID();

      const initialDataProvider: StaticProvider = {
        name: `Provider for ${palette.label}`,
        type: DATA_PROVIDER.FIXED,
        fixed: [{ label: 'New Option 1', value: 'New Option 1' }],
        dependencies: {},
      };

      setDataProviders({
        ...dataProviders,
        [dataProviderId]: initialDataProvider,
      });

      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'multiselect' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        placeholder: '',
        helperText: '',
        colSpan: palette.colSpan,
        isRequired: false,
        infoTooltipText: '',
        isReadOnly: false,
        isHidden: false,
        dataProviderId,
        validations: [],
      };
    }

    case FIELD_TYPES.DATATABLE: {
      const dataProviderId = crypto.randomUUID();

      const initialDataProvider: HttpProvider = {
        type: 'HTTP',
        httpApi: {
          resourceUrl: '',
          connectionName: '',
          method: 'GET' as const,
          headers: [],
          queryParams: [],
          body: undefined,
          isPaginated: false,
          pagination: {
            pageKey: '',
            pageSizeKey: '',
            pageSize: 20,
            totalPagesKey: '',
            totalItemsKey: '',
          },
          responseMapping: {
            fieldMappings: [
              {
                sourceNode: '',
                targetNode: '',
              },
            ],
          },
        },
        dependencies: {},
      };

      setDataProviders({
        ...dataProviders,
        [dataProviderId]: initialDataProvider,
      });

      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'datatable' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        helperText: '',
        colSpan: palette.colSpan,
        isRequired: false,
        infoTooltipText: '',
        isReadOnly: false,
        isHidden: false,
        dataProviderId,
        tableHeight: 400,
        searchable: true,
        pageSize: 20,
      };
    }

    case FIELD_TYPES.RADIO: {
      const dataProviderId = crypto.randomUUID();

      const initialDataProvider: StaticProvider = {
        name: `Provider for ${palette.label}`,
        type: DATA_PROVIDER.FIXED,
        fixed: [{ label: 'New Option 1', value: 'fixedGroupOption1' }],
        dependencies: {},
      };

      setDataProviders({
        ...dataProviders,
        [dataProviderId]: initialDataProvider,
      });

      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'radio' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        helperText: '',
        layout: GROUP_FIELD_ORIENTAION.VERTICAL,
        colSpan: palette.colSpan,
        isRequired: false,
        infoTooltipText: '',
        isReadOnly: false,
        isHidden: false,
        dataProviderId,
        validations: [],
      };
    }

    case FIELD_TYPES.CHECKBOX: {
      const dataProviderId = crypto.randomUUID();

      const initialDataProvider: StaticProvider = {
        name: `Provider for ${palette.label}`,
        type: DATA_PROVIDER.FIXED,
        fixed: [{ label: 'New Option 1', value: 'fixedGroupOption1' }],
        dependencies: {},
      };

      setDataProviders({
        ...dataProviders,
        [dataProviderId]: initialDataProvider,
      });

      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'checkbox' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        helperText: '',
        layout: GROUP_FIELD_ORIENTAION.VERTICAL,
        colSpan: palette.colSpan,
        isRequired: false,
        infoTooltipText: '',
        isReadOnly: false,
        isHidden: false,
        dataProviderId,
        validations: [],
      };
    }

    case FIELD_TYPES.SPACER:
      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'spacer' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        height: SPACER.DEFAULT_HEIGHT,
        colSpan: palette.colSpan,
      };

    case FIELD_TYPES.DATE:
      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'date' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        placeholder: '',
        defaultValue: {
          value: '',
          isExpression: false,
        },
        helperText: '',
        colSpan: palette.colSpan,
        isRequired: false,
        infoTooltipText: '',
        isReadOnly: false,
        isHidden: false,
        validations: [],
        advConfig: {
          dateFormat: DATE_FORMATS.MM_DD_YYYY.display,
          isPastDateAllowed: true,
          isFutureDateAllowed: true,
          minDate: null,
          maxDate: null,
        },
      };

    case FIELD_TYPES.BOOLEAN:
      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'boolean' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        defaultValue: {
          value: '',
          isExpression: false,
        },
        helperText: '',
        colSpan: palette.colSpan,
        isRequired: false,
        infoTooltipText: '',
        isReadOnly: false,
        isHidden: false,
        isChecked: false,
        validations: [],
      };

    case FIELD_TYPES.PASSWORD:
      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'password' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        placeholder: '',
        helperText: '',
        colSpan: palette.colSpan,
        isRequired: false,
        infoTooltipText: '',
        isReadOnly: false,
        isHidden: false,
        advConfig: {
          isPasswordConfirmation: true,
          isAutoGenerationEnabled: true,
        },
        validations: DEFAULT_PASSWORD_VALIDATION,
      };

    case FIELD_TYPES.SENSITIVE:
      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'sensitive' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        helperText: '',
        colSpan: palette.colSpan,
        isRequired: false,
        infoTooltipText: '',
        isHidden: false,
        validations: [],
      };

    case FIELD_TYPES.RICHTEXT:
      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'richtext' as const,
        label: palette.label,
        fieldName: defaultElementId,
        isDisabled: false,
        expressions: getDefaultExpression(palette.fieldType),
        colSpan: palette.colSpan,
        isHidden: false,
        advConfig: {
          richText: undefined,
        },
      };

    case FIELD_TYPES.TERMSANDCONDITIONS:
      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'tnc' as const,
        label: palette.label,
        fieldName: defaultElementId,
        colSpan: palette.colSpan,
        infoTooltipText: '',
        isChecked: false,
        isDisabled: false,
        isReadOnly: false,
        isHidden: false,
        advConfig: {
          richText: undefined,
          tncMessage: 'I have read and agree to the {Terms & Conditions}',
          tncType: TNC_TYPES.TEXT,
          isReadingEnforced: false,
          linkUrl: '',
        },
        validations: [
          {
            type: VALIDATION_TYPES.REQUIRED,
            value: '',
            message: 'This field is required',
          },
        ],
        expressions: getDefaultExpression(palette.fieldType),
      };

    case FIELD_TYPES.CAPTCHA:
      return {
        id: crypto.randomUUID(),
        type: 'field' as const,
        fieldType: 'captcha' as const,
        label: palette.label,
        fieldName: defaultElementId,
        colSpan: palette.colSpan,
        isChecked: false,
        isDisabled: false,
        isReadOnly: false,
        isHidden: false,
        validations: [
          {
            type: VALIDATION_TYPES.REQUIRED,
            value: '',
            message: 'This field is required',
          },
        ],
        expressions: getDefaultExpression(palette.fieldType),
      };

    default:
      throw new Error(`Unknown field type: ${palette.fieldType}`);
  }
};

/* ------------------------------------------------------------------
 *  Helper – remove the dragged field everywhere + clean empty rows
 * ----------------------------------------------------------------*/
function removeFieldAndClean(
  canvas: FieldElement[],
  fieldId: string
): FieldElement[] {
  return canvas
    .map((item) => {
      if (isRow(item)) {
        const kids = (item.children ?? []).filter((c) => c.id !== fieldId);

        return kids.length ? { ...item, children: kids } : null;
      }

      if (isSection(item)) {
        const newRows = (item.children ?? [])
          .map((r) => {
            if (!isRow(r)) {
              return r;
            }

            const kids = (r.children ?? []).filter((c) => c.id !== fieldId);

            return kids.length ? { ...r, children: kids } : null;
          })
          .filter(Boolean) as FieldElement[];

        return { ...item, children: newRows };
      }

      return item;
    })
    .filter(Boolean) as FieldElement[];
}

/* ------------------------------------------------------------------
 *  Helper – recalc availableColumns for every row
 * ----------------------------------------------------------------*/
function withFreshRowCaps(fields: FieldElement[]): FieldElement[] {
  return fields.map((f) => {
    if (isRow(f)) {
      return { ...f, availableColumns: getAvailableColumns(f) };
    }

    if (isSection(f)) {
      const rows = (f.children ?? []).map((r) =>
        isRow(r) ? { ...r, availableColumns: getAvailableColumns(r) } : r
      );

      return { ...f, children: rows };
    }

    return f;
  });
}

/* ------------------------------------------------------------------
 *  Type guards & constants
 * ----------------------------------------------------------------*/
export function isPaletteElement(obj: unknown): obj is PaletteElement {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'source' in obj &&
    (obj as { source?: unknown }).source === FIELD_SOURCE_PALETTE
  );
}

export function getAvailableColumns(row: Row): number {
  if (!row.children?.length) {
    return MAX_COLUMNS;
  }

  const used = row.children.reduce(
    (t, child) => t + ('colSpan' in child ? child.colSpan : 1),
    0
  );

  return MAX_COLUMNS - used;
}

/** True only for the *very first* canvas drop-zone */
function isCanvasLeadingRow(zoneId: string): boolean {
  return zoneId === 'dropzone-row-canvas-leading';
}

export function isRowFull(row: Row): boolean {
  return getAvailableColumns(row) === 0;
}

/**
 * Counts total number of fields (excluding sections and rows) in canvas
 */
export function countFields(canvas: FieldElement[]): number {
  let count = 0;

  canvas.forEach((item) => {
    if (isField(item)) {
      count += 1;
    } else if (isRow(item) && item.children) {
      count += item.children.filter(isField).length;
    } else if (isSection(item) && item.children) {
      item.children.forEach((row) => {
        if (isRow(row) && row.children) {
          count += row.children.filter(isField).length;
        }
      });
    }
  });

  return count;
}

/* ------------------------------------------------------------------
 *  Drag helpers – start & end
 * ----------------------------------------------------------------*/
export function handleDragStart(
  event: DragStartEvent,
  setActiveDragItem: (item: FieldElement | PaletteElement) => void
): void {
  const { current } = event.active.data;

  if (!current) {
    return;
  }

  const { field } = current; // from DroppableFieldWrapper

  if (isPaletteElement(field)) {
    // Clone palette item so canvas ids stay unique
    const clone: PaletteElement = { ...field, id: crypto.randomUUID() };

    setActiveDragItem(clone);
  } else {
    // Existing canvas field
    setActiveDragItem(field as FieldElement);
  }
}

/**
 * Check if a field can fit in a row's available space
 */
function canFieldFitInRow(field: Field, row: Row): boolean {
  return field.colSpan <= getAvailableColumns(row);
}

/**
 * Find the target row for a column drop and validate space
 */
function findTargetRowForColumnDrop(
  canvasFields: FieldElement[],
  targetFieldId: string,
  droppedField: Field
): { row: Row; canFit: boolean } | null {
  const foundItem = canvasFields.find((item) => {
    if (isRow(item)) {
      return item.children?.some((c) => c.id === targetFieldId);
    }

    if (isSection(item)) {
      return (item.children ?? []).some(
        (row) => isRow(row) && row.children?.some((c) => c.id === targetFieldId)
      );
    }

    return false;
  });

  if (!foundItem) {
    return null;
  }

  if (isRow(foundItem)) {
    return {
      row: foundItem,
      canFit: canFieldFitInRow(droppedField, foundItem),
    };
  }

  if (isSection(foundItem)) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const targetRow = (foundItem.children ?? []).find(
      (row) => isRow(row) && row.children?.some((c) => c.id === targetFieldId)
    )!;

    return {
      row: targetRow,
      canFit: canFieldFitInRow(droppedField, targetRow),
    };
  }

  return null;
}

/**
 * Handles drag-end events across all drop zones in the Form Builder canvas.
 *
 * Responsibilities:
 * - Adds new items from the palette into canvas at valid positions.
 * - Moves existing fields to new locations while maintaining structure.
 * - Ensures that:
 *    ─ Sections can only be added to top-level canvas positions.
 *    ─ Fields are always wrapped in a row if dropped into a section.
 *    ─ Fields are inserted inside or between rows/columns as needed.
 *    ─ Fields with colSpan > available space create new rows instead of overflowing.
 *
 * DropZone types handled:
 * - canvas-leading
 * - canvas-trailing-[id]
 * - section-leading-[sectionId]
 * - section-trailing-[rowId]
 * - column-leading-[fieldId]
 * - column-trailing-[fieldId]
 *
 * @param event - dnd-kit DragEndEvent with `over` drop target
 * @param canvasFields - Current array of top-level fields (sections or rows)
 * @param setCanvasFields - State setter to update canvas layout
 * @param activeDragItem - The item being dragged (from palette or canvas)
 * @param setActiveDragItem - Clears drag state on drop
 */
export function handleDropEnd(
  event: DragEndEvent,
  canvasFields: FieldElement[],
  setCanvasFields: (fields: FieldElement[]) => void,
  dataProviders: Record<string, DataProvider>,
  setDataProviders: Dispatch<SetStateAction<Record<string, DataProvider>>>,
  activeDragItem: FieldElement | PaletteElement | null,
  setActiveDragItem: (item: null) => void,
  maxFieldsLimit: number,
  showLimitError?: (bannerType: string) => void
): void {
  const { over } = event;

  // Exit early if no target or drag item.
  if (!over || !activeDragItem) {
    return;
  }

  const isPalette = isPaletteElement(activeDragItem);

  // Check field limit for new fields from palette (excluding sections)
  if (isPalette && activeDragItem.fieldType !== FIELD_TYPES.SECTION) {
    const currentFieldCount = countFields(canvasFields);

    if (currentFieldCount >= maxFieldsLimit) {
      showLimitError?.(FORM_BUILDER_PAGE_BANNERS.FIELD_LIMIT_EXCEEDED);
      setActiveDragItem(null);

      return;
    }
  }

  const overId = over.id.toString();

  // Convert to FieldElement if palette; preserve ID if self-drag.
  const droppedField: FieldElement = isPalette
    ? {
        ...convertPaletteToFieldElement(
          activeDragItem,
          dataProviders,
          setDataProviders
        ),
        id: crypto.randomUUID(),
      }
    : activeDragItem;

  // Track original position in case we need to fallback
  let originTopIdx = -1;
  let originRowId: string | null = null;

  // Find original parent position of dragged item (if it’s a self-drag)
  canvasFields.forEach((item, i) => {
    if (isRow(item) && item.children?.some((c) => c.id === activeDragItem.id)) {
      originTopIdx = i;
      originRowId = item.id;
    }

    if (isSection(item)) {
      const rIdx = (item.children ?? []).findIndex(
        (r) => isRow(r) && r.children?.some((c) => c.id === activeDragItem.id)
      );

      if (rIdx !== -1) {
        originTopIdx = i;
        originRowId = item.children?.[rIdx]?.id ?? null;
      }
    }
  });

  // If palette item, no need to clean; otherwise remove old field from canvas
  const cleaned = isPalette
    ? canvasFields
    : removeFieldAndClean(canvasFields, activeDragItem.id);

  // ROW-LEVEL DROP TARGETS
  if (overId.startsWith('dropzone-row-')) {
    // ──────────────────────────────────────────────
    // Insert into section's leading row dropzone
    if (overId.startsWith('dropzone-row-section-leading-')) {
      if (isSection(droppedField)) {
        return;
      }

      const sectionId = overId.replace('dropzone-row-section-leading-', '');
      const next = deepClone(cleaned);
      const sIdx = next.findIndex((f) => f.id === sectionId && isSection(f));

      if (sIdx === -1) {
        return;
      }

      const section = next[sIdx] as Section;
      const rows = section.children ?? [];

      rows.unshift(createRowWithChildren([droppedField as Field]));
      next[sIdx] = { ...section, children: rows };

      setCanvasFields(withFreshRowCaps(next));
      setActiveDragItem(null);

      return;
    }

    // ──────────────────────────────────────────────
    // Insert at the top of the canvas
    if (isCanvasLeadingRow(overId)) {
      if (isSection(droppedField)) {
        setCanvasFields([droppedField, ...cleaned]);
      } else {
        const newRow = createRowWithChildren([droppedField as Field]);

        setCanvasFields(withFreshRowCaps([newRow, ...cleaned]));
      }

      setActiveDragItem(null);

      return;
    }

    // ──────────────────────────────────────────────
    // Add a new row inside a section (after another row)
    if (overId.startsWith('dropzone-row-section-trailing-')) {
      if (isSection(droppedField)) {
        return;
      }

      const rowId = overId.replace('dropzone-row-section-trailing-', '');
      const newRow = createRowWithChildren([droppedField as Field]);
      const next = deepClone(cleaned);
      let inserted = false;

      next.forEach((sec, sIdx) => {
        if (!isSection(sec)) {
          return;
        }

        const rows = sec.children ?? [];
        const idx = rows.findIndex((r) => r.id === rowId);

        if (idx !== -1) {
          rows.splice(idx + 1, 0, newRow);
          next[sIdx] = { ...sec, children: rows };
          inserted = true;
        }
      });

      // Required: TypeScript cannot infer inserted is truthy
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (inserted) {
        setCanvasFields(withFreshRowCaps(next));
        setActiveDragItem(null);

        return;
      }
    }

    // ──────────────────────────────────────────────
    // Canvas trailing row insertion (most general case)
    const rowId = overId.replace('dropzone-row-canvas-trailing-', '');
    const next = deepClone(cleaned);

    if (isSection(droppedField)) {
      const insertIdx = next.findIndex((f) => f.id === rowId);

      if (insertIdx !== -1) {
        next.splice(insertIdx + 1, 0, droppedField);
        setCanvasFields(withFreshRowCaps(next));
        setActiveDragItem(null);

        return;
      }
    }

    const newRow = createRowWithChildren([droppedField as Field]);

    // Try inserting after a top-level row
    const topRowIdx = next.findIndex((f) => isRow(f) && f.id === rowId);

    if (topRowIdx !== -1) {
      next.splice(topRowIdx + 1, 0, newRow);
      setCanvasFields(withFreshRowCaps(next));
      setActiveDragItem(null);

      return;
    }

    // Try inserting after a section
    const secIdx = next.findIndex((f) => isSection(f) && f.id === rowId);

    if (secIdx !== -1) {
      next.splice(secIdx + 1, 0, newRow);
      setCanvasFields(withFreshRowCaps(next));
      setActiveDragItem(null);

      return;
    }

    // Fallback: Try inserting inside a section after a row
    let inserted = false;

    next.forEach((sec, sIdx) => {
      if (inserted || !isSection(sec)) {
        return;
      }

      const rows = sec.children ?? [];
      const rIdx = rows.findIndex((r) => r.id === rowId);

      if (rIdx !== -1) {
        rows.splice(rIdx + 1, 0, newRow);
        next[sIdx] = { ...sec, children: rows };
        inserted = true;
      }
    });

    // Final fallback: Insert back at origin position
    // Required: TypeScript cannot guarantee inserted is false here
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!inserted && rowId === originRowId) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (originRowId !== null && originTopIdx >= 0) {
        next.splice(Math.min(originTopIdx, next.length), 0, newRow);
        inserted = true;
      } else {
        next.forEach((sec, sIdx) => {
          if (inserted || !isSection(sec)) {
            return;
          }

          const rows = sec.children ?? [];
          const idx = rows.findIndex((r) => r.id === originRowId);

          if (idx !== -1) {
            rows.splice(Math.min(idx, rows.length), 0, newRow);
            next[sIdx] = { ...sec, children: rows };
            inserted = true;
          }
        });
      }
    }

    if (inserted) {
      setCanvasFields(withFreshRowCaps(next));
      setActiveDragItem(null);
    }

    return;
  }

  // COLUMN-LEVEL DROP TARGETS
  const colMatch = overId.match(/^dropzone-column-(leading|trailing)-(.*)$/);

  if (colMatch) {
    const pos = colMatch[1] === 'leading' ? 'leading' : 'trailing';
    const targetFieldId = colMatch[2];
    const next = deepClone(cleaned);

    if (isSection(droppedField)) {
      return;
    }

    const droppedFieldAsField = droppedField as Field;

    // Check if the field can fit in the target row
    const targetRowInfo = findTargetRowForColumnDrop(
      next,
      targetFieldId,
      droppedFieldAsField
    );

    if (!targetRowInfo) {
      setActiveDragItem(null);

      return;
    }

    // If field doesn't fit, create a new row instead
    if (!targetRowInfo.canFit) {
      const newRow = createRowWithChildren([droppedFieldAsField]);

      // Find where to insert the new row
      let inserted = false;

      next.forEach((item, itemIdx) => {
        if (inserted) {
          return;
        }

        if (isRow(item) && item.children?.some((c) => c.id === targetFieldId)) {
          // Insert new row after this row
          next.splice(itemIdx + 1, 0, newRow);
          inserted = true;
        } else if (isSection(item)) {
          const rows = item.children ?? [];
          const rowIdx = rows.findIndex(
            (r) => isRow(r) && r.children?.some((c) => c.id === targetFieldId)
          );

          if (rowIdx !== -1) {
            rows.splice(rowIdx + 1, 0, newRow);
            next[itemIdx] = { ...item, children: rows };
            inserted = true;
          }
        }
      });

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (inserted) {
        setCanvasFields(withFreshRowCaps(next));
        setActiveDragItem(null);
      }

      return;
    }

    // Try inserting into matching row (field fits)
    const insertIntoRow = (row: Row): boolean => {
      if (!row.children) {
        return false;
      }

      const idx = row.children.findIndex((c) => c.id === targetFieldId);

      if (idx === -1) {
        return false;
      }

      row.children.splice(
        pos === 'leading' ? idx : idx + 1,
        0,
        droppedFieldAsField
      );

      return true;
    };

    let done = false;

    next.forEach((item) => {
      if (done) {
        return;
      }

      if (isRow(item)) {
        done = insertIntoRow(item);
      } else if (isSection(item)) {
        (item.children ?? []).forEach((r) => {
          if (!done && isRow(r)) {
            done = insertIntoRow(r);
          }
        });
      }
    });

    // Required: TypeScript cannot infer this is guarded above
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (done) {
      setCanvasFields(withFreshRowCaps(next));
      setActiveDragItem(null);
    }

    return;
  }

  // Catch-all: reset drag state if drop target is invalid
  setActiveDragItem(null);
}
